import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { GooglePlacesService } from 'projects/webapp/app/services/google-places.service';

@Component({
  selector: 'app-google-places-autocomplete',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './google-places-autocomplete.component.html',
  styleUrls: ['./google-places-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GooglePlacesAutocompleteComponent implements OnChanges, OnDestroy {
  @Input() forInput?: HTMLInputElement;
  @Input() countries: string[] = ['ch'];
  @Input() types: string[] = [];
  @Input() detailFields: string[] = ['addressComponents', 'formattedAddress'];
  @Input() debounceMs = 300;
  @Input() minQueryLength = 3;

  @Output() placeSelected = new EventEmitter<google.maps.places.Place>();

  predictions: google.maps.places.PlacePrediction[] = [];
  isOpen = false;
  isLoading = false;
  highlightedIndex = -1;

  private _sessionToken?: google.maps.places.AutocompleteSessionToken;
  private _debounceTimer?: ReturnType<typeof setTimeout>;
  private _blurTimer?: ReturnType<typeof setTimeout>;
  private _attachedInput?: HTMLInputElement;
  private _onInputBound?: (e: Event) => void;
  private _onFocusBound?: () => void;
  private _onBlurBound?: () => void;
  private _onKeyDownBound?: (e: Event) => void;

  constructor(
    private _ngZone: NgZone,
    private _cdr: ChangeDetectorRef,
    private _googlePlacesService: GooglePlacesService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['forInput']) {
      this._detachListeners();
      if (this.forInput) this._attachListeners(this.forInput);
    }
  }

  ngOnDestroy() {
    this._detachListeners();
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    if (this._blurTimer) clearTimeout(this._blurTimer);
  }

  selectPrediction(prediction: google.maps.places.PlacePrediction) {
    this.predictions = [];
    this.isOpen = false;
    this.highlightedIndex = -1;
    this._cdr.markForCheck();
    this._fetchPlaceDetails(prediction.placeId);
  }

  private _attachListeners(inputEl: HTMLInputElement) {
    this._onInputBound = (e: Event) => this._onInput((e.target as HTMLInputElement).value);
    this._onFocusBound = () => this._onFocus();
    this._onBlurBound = () => this._onBlur();
    this._onKeyDownBound = (e: Event) => this._onKeyDown(e as KeyboardEvent);

    inputEl.addEventListener('input', this._onInputBound);
    inputEl.addEventListener('focus', this._onFocusBound);
    inputEl.addEventListener('blur', this._onBlurBound);
    inputEl.addEventListener('keydown', this._onKeyDownBound);

    this._attachedInput = inputEl;
  }

  private _detachListeners() {
    if (!this._attachedInput) return;
    if (this._onInputBound) this._attachedInput.removeEventListener('input', this._onInputBound);
    if (this._onFocusBound) this._attachedInput.removeEventListener('focus', this._onFocusBound);
    if (this._onBlurBound) this._attachedInput.removeEventListener('blur', this._onBlurBound);
    if (this._onKeyDownBound) this._attachedInput.removeEventListener('keydown', this._onKeyDownBound);
    this._attachedInput = undefined;
  }

  private _onInput(value: string) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);

    if (value.trim().length < this.minQueryLength) {
      this.predictions = [];
      this.isOpen = false;
      this.isLoading = false;
      this._cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this._cdr.markForCheck();
    this._debounceTimer = setTimeout(() => this._fetchPredictions(value), this.debounceMs);
  }

  private _onFocus() {
    if (this._blurTimer) clearTimeout(this._blurTimer);
    if (this.predictions.length > 0) {
      this.isOpen = true;
      this._cdr.markForCheck();
    }
  }

  private _onBlur() {
    this._blurTimer = setTimeout(() => {
      this.isOpen = false;
      this._cdr.markForCheck();
    }, 150);
  }

  private _onKeyDown(e: KeyboardEvent) {
    if (!this.isOpen || this.predictions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.highlightedIndex = (this.highlightedIndex + 1) % this.predictions.length;
        this._cdr.markForCheck();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.highlightedIndex = this.highlightedIndex <= 0
          ? this.predictions.length - 1
          : this.highlightedIndex - 1;
        this._cdr.markForCheck();
        break;
      case 'Enter':
        if (this.highlightedIndex >= 0) {
          e.preventDefault();
          this.selectPrediction(this.predictions[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        this.isOpen = false;
        this._cdr.markForCheck();
        break;
    }
  }

  private async _fetchPredictions(query: string) {
    if (!this._sessionToken) {
      this._sessionToken = await this._googlePlacesService.newSessionToken();
    }

    const predictions = await this._googlePlacesService.getPredictions({
      input: query,
      includedRegionCodes: this.countries,
      ...(this.types.length ? { includedPrimaryTypes: this.types } : {}),
      sessionToken: this._sessionToken,
    });

    this._ngZone.run(() => {
      this.isLoading = false;
      if (predictions.length > 0) {
        this.predictions = predictions;
        // The answer can arrive after the customer has already moved on to
        // the next field. Keep it for when they come back (`_onFocus`), but
        // don't leave the list covering the rest of the form.
        this.isOpen = document.activeElement === this._attachedInput;
        this.highlightedIndex = -1;
      } else {
        this.predictions = [];
        this.isOpen = false;
      }
      this._cdr.markForCheck();
    });
  }

  private async _fetchPlaceDetails(placeId: string) {
    const result = await this._googlePlacesService.getPlaceDetails({
      placeId,
      fields: this.detailFields,
      sessionToken: this._sessionToken,
    });

    this._ngZone.run(() => {
      if (result) this.placeSelected.emit(result);
      this._sessionToken = undefined;
    });
  }
}
